import { useEffect } from 'react'

/**
 * `/flinks/callback` — the ONE route this client serves for `callback_paths`
 * (epic #6: "a route WE must serve, not a backend route. Today exactly
 * `["/flinks/callback"]`, exact-match allowlisted server-side"). Runs in the
 * new tab `flinks.ts` opened, not the original one — see that adapter's own
 * docblock for why a `postMessage` back to the opener is the channel, not a
 * popup-location poll.
 *
 * **Parsing is a pure function, tested on its own** — the component's job is
 * only to call it, post the result, and close. `payload.publicToken` may be
 * `undefined`: this route reads the redirect defensively (see `flinks.ts`'s
 * own note on the one unconfirmed detail — which query parameter actually
 * carries the completed login), so a missing value is read, not guessed at
 * or silently substituted.
 */
export function parseFlinksCallback(search: string): {
  publicToken: string | undefined
  metadata: Record<string, string>
} {
  const params = new URLSearchParams(search)
  return {
    publicToken: params.get('login_id') ?? params.get('loginId') ?? undefined,
    metadata: Object.fromEntries(params.entries()),
  }
}

export function FlinksCallback() {
  useEffect(() => {
    // **`window.close()` only when `window.opener` is real** — a browser
    // already refuses to close a tab script did not open, so this is not
    // merely defensive. It is also what keeps this route safe to render
    // outside the popup: `negOnLime.test.tsx`/`compositeContrast.test.tsx`
    // sweep every path in `ROUTE_PATHS`, including this one, and jsdom's
    // `close()` tears down the document for whatever renders next in that
    // same sweep — a real regression, caught by `make test` rather than
    // reasoned about, and the SAME condition that already guards the
    // `postMessage` call fixes it for free rather than adding a second,
    // test-only branch.
    if (window.opener) {
      const payload = parseFlinksCallback(window.location.search)
      window.opener.postMessage({ source: 'flinks-callback', ...payload }, window.location.origin)
      window.close()
    }
  }, [])

  // Reachable in the instant before `window.close()` runs, and whenever this
  // route is reached with no opener at all (a bookmark, a sweep) — a real
  // fallback, not dead code.
  return <p className="page-title">Finishing…</p>
}
